// client/src/components/common/PickerTypedField.tsx
//
// A labelled text box for typing a date (or a date and time) in the pickers' desktop popup
// (ST-00294): Start / End in the range picker, From / To in the date-time range picker, the
// single field of UnifiedDatePicker. It shows the pending value; what is typed is read as you
// type and handed to the picker as soon as it reads as an allowed value. Text that cannot be
// read, or a value the picker refuses (a future day, an end before the start), keeps its text
// and says why — a format complaint only once the entry is long enough, or on leaving the field
// or Enter — and the picker keeps Apply off until it is fixed. Leaving the field tidies a taken
// value into the picker's format. Enter with nothing wrong applies the popup.
//
// Typed text belongs to the value it was typed for: when the value changes from elsewhere (a
// calendar click, a quick range) the box shows the new value.

import { useEffect, useId, useState, type Ref } from 'react';
import { useLatest } from './pickerHooks';

export interface TypedCheck {
  /** Why the text cannot be taken ('' = nothing wrong). */
  error: string;
  /** The text is not (yet) a readable value, as opposed to an allowed-values refusal. */
  unreadable?: boolean;
}

export interface PickerTypedFieldProps {
  label: string;
  /** The pending value as the field prints it ('' when there is none). */
  value: string;
  placeholder?: string;
  /** The end being set: highlighted. */
  active?: boolean;
  /** What is wrong with a typed text (pure; called while rendering). */
  check: (text: string) => TypedCheck;
  /**
   * The text as typed. When it reads as an allowed value the picker takes it and returns the
   * value as the field will print it; otherwise it returns undefined and nothing changes.
   */
  onType: (text: string) => string | undefined;
  onFocus?: () => void;
  /** Enter with nothing wrong. */
  onEnter?: () => void;
  /** Told whether the field holds something that cannot be taken (the popup keeps Apply off). */
  onInvalidChange?: (invalid: boolean) => void;
  inputRef?: Ref<HTMLInputElement>;
  /** An unreadable entry this long already complains while typing (a full YYYY/MM/DD). */
  completeLength?: number;
}

interface Entry {
  text: string;
  /** The printed value the text stands for. */
  at: string;
  /** Left the field / pressed Enter: complaints show. */
  settled: boolean;
}

export default function PickerTypedField({
  label,
  value,
  placeholder,
  active = false,
  check,
  onType,
  onFocus,
  onEnter,
  onInvalidChange,
  inputRef,
  completeLength = 10,
}: PickerTypedFieldProps) {
  const id = useId();
  const errorId = useId();
  const [entry, setEntry] = useState<Entry | null>(null);
  const editing = entry !== null && entry.at === value;
  const text = editing ? entry.text : value;
  const { error, unreadable = false } = editing ? check(entry.text) : { error: '' };
  const shownError =
    !!error && (!unreadable || entry!.settled || entry!.text.trim().length >= completeLength);

  const report = useLatest(onInvalidChange);
  useEffect(() => {
    report.current?.(!!error);
  }, [error, report]);
  useEffect(() => () => report.current?.(false), [report]);

  // Leaving the field: a taken value is printed in the picker's format; a problem is explained.
  const settle = () => {
    if (!editing) return;
    if (error) setEntry({ ...entry, settled: true });
    else setEntry(null);
  };

  return (
    <div className={`picker-typed${active ? ' is-active' : ''}`}>
      <label htmlFor={id} className="picker-typed__label">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        // The box fills its column (CSS) but does not widen it: the calendar sets the width.
        size={1}
        className="picker-typed__input"
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={shownError || undefined}
        aria-describedby={shownError ? errorId : undefined}
        onFocus={onFocus}
        onChange={(e) => {
          const next = e.target.value;
          const taken = onType(next);
          setEntry({ text: next, at: taken ?? value, settled: false });
        }}
        onBlur={settle}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          settle();
          if (!error) onEnter?.();
        }}
      />
      {shownError && (
        <p id={errorId} className="picker-typed__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
